import React, { useState } from 'react';
import moment from 'moment';
import { Box, Typography } from '@material-ui/core';
import { CustomIconButton, CustomTimeline } from '../../../../Components';
import { useSelectedTheme, useTranslate } from '../../../../Hooks';
import { AgentWalletConfigurationPath } from '../I18n/AgentWalletConfigurationLocale';

import { ArrowNarrowRight, DropdownIcon } from '../../../../assets/icons';

import useStyles from './styles';
const safeParseJSON = (str) => {
  return str ? JSON.parse(str) : {};
};

const getChangeFields = (before, after) => {
  const beforeObj = safeParseJSON(before) || {};
  const afterObj = safeParseJSON(after) || {};
  const fields = new Set([...Object.keys(beforeObj), ...Object.keys(afterObj)]);

  return Array.from(fields)
    .filter((field) => {
      const beforeVal = beforeObj[field] ?? '';
      const afterVal = afterObj[field] ?? '';
      return JSON.stringify(beforeVal) !== JSON.stringify(afterVal);
    })
    .map((field) => ({
      fieldName: field,
      changedFrom: beforeObj[field] ?? '',
      changedTo: afterObj[field] ?? '',
    }));
};
const mapApiDataToTimeline = (apiData) => {
  return apiData?.map((entry, idx) => ({
    dfMTransactionId: `${entry.action}-${idx}`,
    createdBy: entry.userName,
    requestType: entry.action,
    createdOn: moment(entry.createdOn, 'DD MMM YYYY hh:mm A').toISOString(),
    changeFields:
      entry.action.toLowerCase() === 'read'
        ? []
        : getChangeFields(entry.before, entry.after),
  }));
};

const objectIfIsArray = (arr) => arr.join(', ');

function HistoryTimelineItem({ historyData }) {
  const timelineItems = mapApiDataToTimeline(historyData);
  const styles = useStyles();

  return (
    <CustomTimeline
      timelineData={timelineItems}
      component={TimelineItemContent}
      timelinePrefixComponent={TimelinePrefixContent}
      timelineDotTopMargin='15px'
      timelineContentClasses={styles.historyTimelineItemContent}
    />
  );
}

export default HistoryTimelineItem;

function TimelineItemContent({ item }) {
  const [openTransactionId, setOpenTransactionId] = useState(null);
  const { translate } = useTranslate(AgentWalletConfigurationPath);

  const toggleTransaction = () => {
    setOpenTransactionId((prev) =>
      prev === item.dfMTransactionId ? null : item.dfMTransactionId
    );
  };

  const hasChanges =
    Array.isArray(item.changeFields) && item.changeFields.length > 0;
  const isOpen = openTransactionId === item.dfMTransactionId;
  const {
    theme: { palette },
  } = useSelectedTheme();
  const styles = useStyles();

  return (
    <Box className={styles.wrapperHistoryTimelineItemContent}>
      <Box className={styles.HistoryTimelineItemContent}>
        <Box className={styles.containerHistoryInformation}>
          <Box className={styles.containerHeaderHistory}>
            <Box className={styles.containerImage}>
              <img
                className={styles.imageWrapper}
                src={'/images/contacts/MaleAvatar.svg'}
                alt='avatar'
              />
            </Box>
            <Typography className={styles.createdByTitle}>
              {item.createdBy}
            </Typography>
          </Box>

          <Box className={styles.containerTypeHistory}>
            <Typography className={styles.typeTitle}>Type:</Typography>
            <Typography className={styles.typeValue}>
              {item.requestType}
            </Typography>
          </Box>
        </Box>

        {hasChanges && (
          <CustomIconButton
            variant='text'
            size='none'
            boxShadow='none'
            color='tertiaryColor'
            hideHoverBg
            onClick={toggleTransaction}
          >
            <DropdownIcon
              width='20'
              height='20'
              fill={palette.utility.utility_gray_500}
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
              }}
            />
          </CustomIconButton>
        )}
      </Box>

      {isOpen && hasChanges && (
        <Box className={styles.containerTableContactFieldChange}>
          <Box className={styles.headerTable}>
            <Box className={styles.tableCellTitle}>Field Name</Box>
            <Box className={styles.tableCellTitle}>Changed From</Box>
            <Box className={styles.tableCellTitleBlank}></Box>
            <Box className={styles.tableCellTitle}>Changed To</Box>
          </Box>

          {item.changeFields.map((field, idx) => (
            <Box
              key={idx}
              className={styles.bodyTable}
            >
              <Box className={styles.tableCellTitleField}>
                {translate(field.fieldName)}
              </Box>

              <Box className={styles.tableCellValue}>
                {typeof field.changedFrom === 'string' ||
                typeof field.changedFrom === 'number'
                  ? field.changedFrom
                  : Array.isArray(field.changedFrom)
                  ? objectIfIsArray(field.changedFrom)
                  : JSON.stringify(field.changedFrom)}
              </Box>

              <Box className={styles.iconWrapper}>
                <ArrowNarrowRight
                  width='20'
                  height='20'
                  fill={palette.button.tertiaryGray_fg}
                />
              </Box>

              <Box className={styles.tableCellValue}>
                {typeof field.changedTo === 'string' ||
                typeof field.changedTo === 'number'
                  ? field.changedTo
                  : Array.isArray(field.changedTo)
                  ? objectIfIsArray(field.changedTo)
                  : JSON.stringify(field.changedTo)}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function TimelinePrefixContent({ item }) {
  const formatDate = (dateTime) => {
    if (!dateTime) return { day: null, time: null, year: null };
    const m = moment(dateTime).locale('en');
    return {
      day: m.format('D MMM'),
      year: m.format('YYYY'),
      time: m.format('h:mm a'),
    };
  };

  const styles = useStyles();
  const { day, year, time } = formatDate(item.createdOn);

  return (
    <Box className={styles.containerTimelinePrefixContent}>
      <Box className={styles.dayTitle}>{day}</Box>
      <Box className={styles.yearTitle}>{year}</Box>
      <Box className={styles.timeTitle}>{time}</Box>
    </Box>
  );
}
